import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { colors, spacing, typography, motion } from './theme';
import { BrandLogo } from './components/BrandLogo';

const label = {
  fontFamily: typography.label.family,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  fontSize: 11,
  fontWeight: 600,
  color: colors.gold,
};

const display = {
  fontFamily: typography.display.family,
  fontWeight: 500,
  letterSpacing: '-0.015em',
  lineHeight: 1.08,
  color: colors.paper,
};

const container = {
  width: '100%',
  maxWidth: 880,
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingLeft: spacing.lg,
  paddingRight: spacing.lg,
  boxSizing: 'border-box',
};

const sectionHeading = {
  fontFamily: typography.display.family,
  fontWeight: 500,
  fontSize: 28,
  color: colors.paper,
  letterSpacing: '-0.01em',
  lineHeight: 1.2,
  marginTop: spacing.xl,
  marginBottom: spacing.md,
};

const bodyText = {
  fontFamily: typography.body.family,
  fontSize: 16,
  lineHeight: 1.75,
  color: colors.bone,
  margin: 0,
  marginBottom: spacing.md,
};

const listStyle = {
  fontFamily: typography.body.family,
  fontSize: 16,
  lineHeight: 1.85,
  color: colors.bone,
  paddingLeft: 22,
  marginTop: spacing.sm,
  marginBottom: spacing.md,
};

const hairline = { height: 1, width: '100%', background: colors.hairline };

export default function SmsTerms() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'SMS Terms of Communication — Black-Ops Luxury Car Service';
  }, []);

  return (
    <main
      data-testid="sms-terms-root"
      style={{
        backgroundColor: colors.void,
        color: colors.bone,
        minHeight: '100vh',
        fontFamily: typography.body.family,
      }}
    >
      {/* Header */}
      <header
        style={{
          borderBottom: `1px solid ${colors.hairlineSoft}`,
          paddingTop: 18,
          paddingBottom: 18,
        }}
      >
        <div
          style={{
            ...container,
            maxWidth: 1280,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: spacing.md,
          }}
        >
          <Link to="/" data-testid="sms-nav-home" aria-label="Black-Ops home">
            <BrandLogo size={150} />
          </Link>
          <Link
            to="/"
            data-testid="sms-back-home"
            style={{
              ...label,
              color: colors.champagne,
              border: `1px solid ${colors.champagne}`,
              paddingTop: 11,
              paddingBottom: 11,
              paddingLeft: 22,
              paddingRight: 22,
              transition: `background ${motion.fast}ms ${motion.easeOut}, color ${motion.fast}ms ${motion.easeOut}`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = colors.champagne;
              e.currentTarget.style.color = colors.void;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = colors.champagne;
            }}
          >
            ← Return to Site
          </Link>
        </div>
      </header>

      {/* Body */}
      <article style={{ ...container, paddingTop: spacing.xxl, paddingBottom: spacing.xxxl }}>
        <div style={{ ...label, marginBottom: 14 }}>Legal · SMS</div>
        <h1
          style={{
            ...display,
            fontSize: 'clamp(36px, 5vw, 64px)',
            margin: 0,
            marginBottom: spacing.md,
          }}
        >
          SMS Terms of Communication
        </h1>
        <p
          style={{
            fontFamily: typography.body.family,
            color: colors.ash,
            fontSize: 16,
            lineHeight: 1.6,
            margin: 0,
            marginBottom: spacing.xl,
          }}
        >
          Black-Ops Luxury Car Service
        </p>

        <h2 style={sectionHeading} data-testid="sms-consent">Consent for SMS Communication</h2>
        <p style={bodyText}>
          The information (phone numbers) obtained as part of the SMS consent
          process will not be shared with third parties for marketing purposes.
        </p>

        <h2 style={sectionHeading} data-testid="sms-types">Types of SMS Communications</h2>
        <p style={bodyText}>
          If you have consented to receive text messages from Black-Ops Luxury
          Car Service, you may receive messages related to the following:
        </p>
        <ul style={listStyle}>
          <li>Reservation &amp; appointment reminders</li>
          <li>Reservation confirmations</li>
          <li>Driver arrival alerts</li>
          <li>Ride receipts and post-ride follow-up messages</li>
          <li>Billing inquiries</li>
          <li>Promotions or offers (if applicable, with separate explicit opt-in)</li>
        </ul>

        <h2 style={sectionHeading} data-testid="sms-frequency">Message Frequency</h2>
        <p style={bodyText}>
          Message frequency may vary depending on the type of communication.
          For example, you may receive up to four (4) SMS messages per week
          related to your reservation reminders, follow-up messages, marketing,
          or billing inquiries.
        </p>

        <h2 style={sectionHeading} data-testid="sms-fees">Potential Fees for SMS Messaging</h2>
        <p style={bodyText}>
          Please note that standard message and data rates may apply, depending
          on your carrier&rsquo;s pricing plan. These fees may vary if the
          message is sent domestically or internationally.
        </p>

        <h2 style={sectionHeading} data-testid="sms-opt-in">Opt-In Method</h2>
        <p style={bodyText}>
          You may opt-in to receive SMS messages from Black-Ops Luxury Car
          Service in the following ways:
        </p>
        <ul style={listStyle}>
          <li>By submitting an online reservation form on our website</li>
        </ul>

        <h2 style={sectionHeading} data-testid="sms-opt-out">Opt-Out Method</h2>
        <p style={bodyText}>
          You can opt out of receiving SMS messages at any time. To do so,
          simply reply <strong style={{ color: colors.champagne }}>STOP</strong> to
          any SMS message you receive. Alternatively, you can contact us
          directly to request removal from our messaging list.
        </p>

        <h2 style={sectionHeading} data-testid="sms-help">Help</h2>
        <p style={bodyText}>
          If you are experiencing any issues, you can reply with the keyword{' '}
          <strong style={{ color: colors.champagne }}>HELP</strong>. You may
          also reply <strong style={{ color: colors.champagne }}>STOP</strong> at
          any time to unsubscribe from future SMS messages. You can also reach
          us directly at our concierge line{' '}
          <a
            href="tel:+18442556771"
            style={{ color: colors.champagne, borderBottom: `1px solid ${colors.champagne}` }}
          >
            844-BLK-OPS1
          </a>{' '}
          or by email at{' '}
          <a
            href="mailto:pickup@blk-ops1.com"
            style={{ color: colors.champagne, borderBottom: `1px solid ${colors.champagne}` }}
          >
            pickup@blk-ops1.com
          </a>
          .
        </p>

        <h2 style={sectionHeading} data-testid="sms-additional">Additional Options</h2>
        <p style={bodyText}>
          If you do not wish to receive SMS messages, you can choose not to
          check the SMS consent box on our forms. You will still be able to
          reserve and ride with us — confirmations will simply be delivered by
          email or phone instead.
        </p>

        <h2 style={sectionHeading} data-testid="sms-disclosures">Standard Messaging Disclosures</h2>
        <div
          data-testid="sms-disclosure-card"
          style={{
            background: colors.onyx,
            border: `1px solid ${colors.steel}`,
            padding: spacing.lg,
            marginTop: spacing.md,
            marginBottom: spacing.lg,
          }}
        >
          <ul
            style={{
              ...listStyle,
              margin: 0,
              listStyle: 'none',
              paddingLeft: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {[
              { text: 'Message and data rates may apply.' },
              { text: 'You can opt out at any time by texting STOP.' },
              {
                text: 'For assistance, text HELP or visit our',
                links: [
                  { to: '/privacy', label: 'Privacy Policy', testid: 'sms-disclosure-privacy-link' },
                  { to: '/sms-terms', label: 'Terms and Conditions', testid: 'sms-disclosure-terms-link' },
                ],
              },
              { text: 'Message frequency may vary.' },
            ].map(({ text, links }) => (
              <li
                key={text}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  fontSize: 15,
                  lineHeight: 1.65,
                }}
              >
                <span
                  aria-hidden
                  style={{
                    color: colors.champagne,
                    flexShrink: 0,
                    fontWeight: 600,
                    paddingTop: 2,
                  }}
                >
                  ✦
                </span>
                <span>
                  {text}
                  {links && (
                    <>
                      {' '}
                      <Link
                        to={links[0].to}
                        data-testid={links[0].testid}
                        style={{ color: colors.champagne, borderBottom: `1px solid ${colors.champagne}` }}
                      >
                        {links[0].label}
                      </Link>
                      {' and '}
                      <Link
                        to={links[1].to}
                        data-testid={links[1].testid}
                        style={{ color: colors.champagne, borderBottom: `1px solid ${colors.champagne}` }}
                      >
                        {links[1].label}
                      </Link>
                      {' pages.'}
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div style={{ ...hairline, marginTop: spacing.lg, marginBottom: spacing.md }} />

        <p
          style={{
            ...label,
            color: colors.ash,
            letterSpacing: '0.3em',
            fontSize: 10,
            margin: 0,
          }}
        >
          <Link to="/privacy" data-testid="sms-privacy-link" style={{ color: colors.bone }}>
            Privacy Policy
          </Link>
          {' · '}
          Last updated · {new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' })}
        </p>
      </article>

      {/* Footer */}
      <footer
        style={{
          borderTop: `1px solid ${colors.hairlineSoft}`,
          paddingTop: spacing.xl,
          paddingBottom: spacing.xl,
        }}
      >
        <div
          style={{
            ...container,
            maxWidth: 1280,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: spacing.md,
            ...label,
            color: colors.ash,
            fontSize: 10,
          }}
        >
          <span>© {new Date().getFullYear()} Black-Ops Luxury Car Service, LLC.</span>
          <Link to="/" data-testid="sms-footer-home" style={{ color: colors.bone }}>
            Return to Site
          </Link>
          <span>www.black-opslcs.com</span>
        </div>
      </footer>
    </main>
  );
}
